import {
  Alert,
  Button,
  Grid,
  GridItem,
  Modal,
  Split,
  SplitItem,
  Text,
  TextArea,
  TextContent,
  TextVariants,
  ValidatedOptions,
} from '@patternfly/react-core'

import { useQuery } from '@tanstack/react-query'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Alarms,
  AlarmsAPI,
  AlarmView,
  DowntimeAero,
  DowntimeAPI,
  DowntimeComponentes,
  DowntimeTipos,
  Turbines,
} from '../../client'
import { ActionState, requestStateMessages } from '../../components/actionState'
import DatetimeRangePicker from '../../components/calendar/DatetimeRangePicker'
import EntityTable from '../../components/EntityTable'
import Select from '../../components/selects/Select'
import PermissionContext from '../../contexts/PermissionContext'
import SitesContext from '../../contexts/SitesContext'
import useMobile from '../../hooks/useMobile'
import { queryClient, QUERY_OPTIONS } from '../../services/api'
import calcVerticalHeight from '../../utils/calcVerticalHeight'
import { formatDateWithoutTime, toISOString } from '../../utils/formatDate'
import {
  CACHE_KEY_ALARMS,
  CACHE_KEY_WTG,
  DEFAULT_DATA,
  Responsabilidade,
  RESPONSABILIDADES,
} from './constants'
import PlotExpectedEnergy from './PlotExpectedEnergy'
import { formatAlarmDate } from './TableAero'
import useData, { keyFromDate } from './useData'
interface DowntimeTiposSelect extends DowntimeTipos {
  label_categoria: string
}

const MODAL_WRAPPER_STYLE = {
  minHeight: '20rem',
  height: calcVerticalHeight({ customOffset: 3, pgHasFilter: false }),
}

const ModalAero = ({
  isModalOpen,
  handleModalToggle,
  eventSelected,
  selectedDate,
  handleDeleteClick,
  modalConfirmation,
  aeroFilterCreateSelected,
}: {
  isModalOpen: boolean
  handleModalToggle: () => void
  eventSelected?: DowntimeAero
  selectedDate: Date
  handleDeleteClick?: (item: string) => void
  modalConfirmation: React.ReactNode
  aeroFilterCreateSelected?: Turbines
}) => {
  const { turbs, site } = useContext(SitesContext)
  const { me } = useContext(PermissionContext)
  const { componentes, tipos, dataIsLoading } = useData(selectedDate)
  const isMobile = useMobile()

  const tiposSelect: DowntimeTiposSelect[] = useMemo(() => {
    return tipos
      .filter(tipo => tipo.tipo === 'AERO')
      .map(tipo => {
        return { ...tipo, label_categoria: tipo.categoria }
      })
  }, [tipos, dataIsLoading])

  const [minDate, maxDate] = getMonthStartAndEnd(selectedDate)

  const defaultPeriod = { tsIn: minDate, tsFin: maxDate }

  const [tsIn, setTsIn] = useState<Date>(
    eventSelected?.ts_in ? new Date(eventSelected.ts_in) : defaultPeriod.tsIn
  )
  const [tsFin, setTsFin] = useState<Date>(
    eventSelected?.ts_fin ? new Date(eventSelected.ts_fin) : defaultPeriod.tsFin
  )
  const [aero, setAero] = useState<Turbines>(
    turbs.find(t => t.turb_id === eventSelected?.turb_id) ?? turbs[0]
  )
  const [selectedTipo, setSelectedTipo] = useState(
    tiposSelect.find(t => t.id === eventSelected?.tipo_id) ?? tiposSelect[0]
  )
  const [selectedComponent, setSelectedComponent] = useState(
    componentes.find(t => t.id === eventSelected?.component_id) ?? componentes[0]
  )
  const [description, setDescription] = useState<string | null>(eventSelected?.descricao ?? '')

  const responsabilidadeVazia: Responsabilidade = { id: false, nome: '' }
  const responsabilidadesComVazia = [responsabilidadeVazia, ...RESPONSABILIDADES]

  const [responsabilidade, setResponsabilidade] = useState<Responsabilidade>(responsabilidadeVazia)
  const [alertActive, setAlertActive] = useState(false)
  const [isError406, setIsError406] = useState(false)
  const [requestState, setRequestState] = useState<ActionState>('idle')
  const handleSubmit = async () => {
    if (description === '') {
      setAlertActive(true)
      return
    }
    if (responsabilidade.nome === responsabilidadeVazia.nome || responsabilidade.nome === '') {
      setAlertActive(true)
      return
    }
    setRequestState('pending')
    const requestBody = {
      ts_in: toISOString(tsIn),
      ts_fin: toISOString(tsFin),
      turb_id: aero.turb_id,
      tipo_id: selectedTipo.id,
      component_id: selectedComponent.id,
      descricao: description,
      username: me.current_user.username,
      id: eventSelected?.id,
      responsabilidade_re: responsabilidade.id,
    }
    const requestMethod = eventSelected
      ? DowntimeAPI.updateDowntimeAero
      : DowntimeAPI.createDowntimeAero

    try {
      await requestMethod({ siteId: site.site_id, requestBody })
    } catch (error: any) {
      console.log(error.status)
      setIsError406(error.status === 406)
      setRequestState('failure')
      setAlertActive(true)
      return
    }
    setRequestState('success')

    queryClient.invalidateQueries([CACHE_KEY_WTG, site.site_id, keyFromDate(selectedDate, 1)])
  }

  const closeModal = () => {
    setTsIn(defaultPeriod.tsIn)
    setTsFin(defaultPeriod.tsFin)
    setAlertActive(false)
    setIsError406(false)
    setRequestState('idle')
    handleModalToggle()
    setResponsabilidade(responsabilidadeVazia)
  }

  useEffect(() => {
    if (requestState === 'success') {
      const timerCloseModalAero = setTimeout(() => {
        closeModal()
      }, 750)
      return () => clearTimeout(timerCloseModalAero)
    }
  }, [requestState])

  useEffect(() => {
    setAlertActive(false)
    setRequestState('idle')
  }, [description, tsIn, tsFin, aero, selectedTipo, selectedComponent, responsabilidade])

  useEffect(() => {
    if (!eventSelected && !aeroFilterCreateSelected) {
      setSelectedTipo(tiposSelect[1])
      setAero(turbs[0])
      setSelectedComponent(componentes[0])
      setTsIn(defaultPeriod.tsIn)
      setTsFin(defaultPeriod.tsFin)
      setDescription('')
      setResponsabilidade(responsabilidadeVazia)
      return
    }
    setSelectedTipo(tiposSelect.find(t => t.id === eventSelected?.tipo_id) ?? tiposSelect[1])
    setSelectedComponent(
      componentes.find(t => t.id === eventSelected?.component_id) ?? componentes[0]
    )
    setDescription(eventSelected?.descricao ?? '')
    setAero(
      turbs.find(
        t => t.turb_id === (eventSelected?.turb_id ?? aeroFilterCreateSelected?.turb_id)
      ) ?? turbs[0]
    )
    setResponsabilidade(
      RESPONSABILIDADES.find(r => eventSelected?.responsabilidade_re === r.id) ??
        responsabilidadeVazia
    )
    if (eventSelected?.ts_in && new Date(eventSelected.ts_in).getTime() !== tsIn.getTime())
      setTsIn(new Date(eventSelected.ts_in))

    if (eventSelected?.ts_fin && new Date(eventSelected.ts_fin).getTime() !== tsFin.getTime())
      setTsFin(new Date(eventSelected.ts_fin))
  }, [eventSelected, dataIsLoading, isModalOpen, aeroFilterCreateSelected])

  const { isLoading: alarmsIsLoading, data: alarmView } = useQuery<AlarmView>(
    [CACHE_KEY_ALARMS, site?.site_id, tsIn, tsFin, aero?.turb_id ?? 0],
    async () =>
      AlarmsAPI.getAll({
        siteId: site.site_id,
        tsIn: formatDateWithoutTime(tsIn),
        tsFin: formatDateWithoutTime(tsFin),
        turbId: aero.turb_id,
        limit: 5000,
        offset: 0,
      }),
    {
      ...QUERY_OPTIONS,
      enabled: !!site?.site_id,
      placeholderData: DEFAULT_DATA.alarms,
    }
  )

  const alarms = useMemo(() => {
    if (alarmsIsLoading) return []

    const alarms = alarmView?.alarms ?? []

    return [...alarms].filter(a => new Date(a.ts_in) >= tsIn && new Date(a.ts_fin) <= tsFin)
  }, [alarmsIsLoading, alarmView, aero, tsIn, tsFin])

  return (
    <Modal
      width='95%'
      aria-label='expected-energy-modal'
      isOpen={isModalOpen}
      onClose={handleModalToggle}
      id='ModalEnergyExpected'
      showClose={false}
      actions={[
        <div
          key={'modal-aero'}
          style={{ justifyContent: 'space-between', display: 'flex', width: '100%' }}
        >
          <Split hasGutter>
            <SplitItem>
              <Button variant='primary' onClick={handleSubmit}>
                Salvar
              </Button>
            </SplitItem>
            <SplitItem>
              <Button variant='secondary' isDanger onClick={closeModal}>
                Cancelar
              </Button>
            </SplitItem>
          </Split>
          {handleDeleteClick && eventSelected && (
            <Button
              variant='danger'
              isDanger
              onClick={() => {
                handleDeleteClick(eventSelected?.id as string)
              }}
            >
              Deletar
            </Button>
          )}
        </div>,
      ]}
    >
      {modalConfirmation}
      <div style={MODAL_WRAPPER_STYLE}>
        <Grid hasGutter>
          <GridItem md={12} lg={5}>
            <GridItem>
              <TextContent className='pf-v5-u-mb-md'>
                <Text component={TextVariants.h1}>Cadastro Paradas Aerogerador</Text>
                <Text component={TextVariants.pre}>{eventSelected?.id}</Text>
              </TextContent>
            </GridItem>
            <GridItem>
              {requestStateMessages()[requestState]}
              {alertActive && (
                <GridItem lg={12}>
                  {tsIn > tsFin && (
                    <Alert
                      variant='danger'
                      className='pf-v5-u-my-sm'
                      isInline
                      title='Data inicial deve ser menor que data final'
                    />
                  )}
                  {description === '' && (
                    <Alert
                      variant='danger'
                      className='pf-v5-u-my-sm'
                      isInline
                      title='Preencha o campo de Descrição'
                    />
                  )}
                  {(responsabilidade.nome === responsabilidadeVazia.nome ||
                    responsabilidade.nome === '') && (
                    <Alert
                      variant='danger'
                      className='pf-v5-u-my-sm'
                      isInline
                      title='Selecione uma Responsabilidade'
                    />
                  )}
                  {isError406 && (
                    <Alert
                      variant='danger'
                      isInline
                      className='pf-v5-u-my-sm'
                      title='Já existe um evento com essas características'
                    />
                  )}
                </GridItem>
              )}
            </GridItem>
            <GridItem>
              <Grid hasGutter>
                <GridItem lg={6}>
                  <span>Aero</span>
                  <Select<Turbines>
                    items={turbs}
                    itemValueName={'name'}
                    itemKeyName={'turb_id'}
                    selected={aero}
                    onChange={setAero}
                    selectProps={{ menuAppendTo: 'parent' }}
                    enableFilter
                    placeholderText='Aero'
                  />
                </GridItem>
                <GridItem lg={6}>
                  <span>Responsabilidade</span>
                  <Select<Responsabilidade>
                    items={responsabilidadesComVazia}
                    itemValueName={'nome'}
                    itemKeyName={'id'}
                    selected={responsabilidade}
                    onChange={setResponsabilidade}
                    selectProps={{ menuAppendTo: 'parent' }}
                  />
                </GridItem>
                <GridItem lg={12}>
                  <span>Período</span>
                  <DatetimeRangePicker
                    value={{ startDate: tsIn, endDate: tsFin }}
                    onChange={(newStart: Date, newEnd: Date) => {
                      setTsIn(newStart)
                      setTsFin(newEnd)
                    }}
                    variant='label'
                    calendarVariant='datetime'
                    minEnabled={minDate}
                    maxEnabled={maxDate}
                    allowEqualDates
                  />
                </GridItem>
                <GridItem lg={6}>
                  <span>Tipo</span>
                  <Select<DowntimeTiposSelect>
                    items={tiposSelect}
                    itemValueName={'label_categoria'}
                    itemKeyName={'id'}
                    selected={selectedTipo}
                    enableFilter
                    selectProps={{ menuAppendTo: 'parent' }}
                    onChange={setSelectedTipo}
                  />
                </GridItem>
                <GridItem lg={6}>
                  <span>Componente</span>
                  <Select<DowntimeComponentes>
                    items={componentes}
                    itemValueName={'component'}
                    itemKeyName={'id'}
                    selected={selectedComponent}
                    enableFilter
                    onChange={setSelectedComponent}
                    selectProps={{ menuAppendTo: 'parent' }}
                  />
                </GridItem>
                <GridItem lg={12}>
                  <span>Descrição</span>
                  <TextArea
                    placeholder='Descrição'
                    height={100}
                    type='text'
                    aria-label='description input field'
                    value={description ?? ''}
                    onChange={(_, text) => setDescription(text)}
                    resizeOrientation='vertical'
                    validated={
                      description === '' && alertActive
                        ? ValidatedOptions.error
                        : ValidatedOptions.default
                    }
                  />
                </GridItem>
                {alarms ? (
                  <GridItem lg={12}>
                    <span>Alarmes</span>
                    <EntityTable<Alarms>
                      items={alarms ?? []}
                      itemKeyName={'site_id'}
                      onSelect={a => {
                        const descricao = `${description} Alarme: '${a.error}'  Descrição: '${a.description}' `
                        setDescription(descricao)
                      }}
                      columnConfig={[
                        { key: 'ts_in', description: 'Inicio', formatter: formatAlarmDate },
                        { key: 'ts_fin', description: 'Fim', formatter: formatAlarmDate },
                        { key: 'dur_min', description: 'Duração [min]' },
                        { key: 'error', description: 'Alarme' },
                        { key: 'description', description: 'Descrição' },
                      ]}
                      style={{ maxHeight: '10rem', height: '5rem' }}
                    />
                  </GridItem>
                ) : null}
              </Grid>
            </GridItem>
          </GridItem>
          {isMobile ? null : (
            <GridItem md={12} lg={7}>
              <PlotExpectedEnergy
                event={{
                  aero: aero?.name || '',
                  categoria: '',
                  componente: '',
                  inicio: tsIn.toISOString(),
                  fim: tsFin.toISOString(),
                  usuario: '',
                  turb_id: aero?.turb_id,
                  responsabilidade_re: responsabilidade.id,
                  descricao: description,
                  tipo_id: selectedTipo?.id,
                  ts_in: tsIn.toISOString(),
                  ts_fin: tsFin.toISOString(),
                  component_id: selectedComponent?.id,
                  username: me.current_user.username,
                }}
                tsIn={tsIn}
                tsFin={tsFin}
                setTsIn={setTsIn}
                setTsFin={setTsFin}
                alarms={alarms}
                style={{ width: '100%', height: calcVerticalHeight({ customOffset: 3 }) }}
              />
            </GridItem>
          )}
        </Grid>
      </div>
    </Modal>
  )
}

const getMonthStartAndEnd = (selectedDate: Date): [startDate: Date, endDate: Date] => {
  const year = selectedDate.getFullYear()
  const month = selectedDate.getMonth()
  const startDate = new Date(year, month, 1)
  const endDate = new Date(year, month + 1, 0)
  return [startDate, endDate]
}

export default ModalAero
